import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Shared action rows for forms, cards, sections, and heroes.
 *
 * Variants:
 *   - `registration` — grid gap-3, spacing above actions.
 *   - `stacked` — full-width stacked actions with card body rhythm.
 *   - `stretch` — stretched vertical column (MFA-style primary + link buttons).
 *   - `wrap` — section CTAs that wrap (enrollment, team, wallet shells).
 *   - `toolbar` — centered wrapping row (statement footers, dialog action bands).
 *   - `hero` — hero content actions with tighter gap + top margin.
 *   - `segment` — stretched column with standard field gap (payment card actions).
 *   - `joined` — related controls rendered as one joined button group (absorbs ButtonGroup).
 *   - `separated` — related controls rendered as separated buttons but one semantic group.
 */
const formActionsVariants = cva("cw-v2-form-actions", {
  variants: {
    variant: {
      registration: "cw-v2-form-actions--registration",
      stacked: "cw-v2-form-actions--stacked",
      stretch: "cw-v2-form-actions--stretch",
      wrap: "cw-v2-form-actions--wrap",
      toolbar: "cw-v2-form-actions--toolbar",
      hero: "cw-v2-form-actions--hero",
      segment: "cw-v2-form-actions--segment",
      joined: "cw-v2-form-actions--button-group cw-v2-form-actions--joined",
      separated: "cw-v2-form-actions--button-group cw-v2-form-actions--separated",
    },
  },
  defaultVariants: {
    variant: "registration",
  },
});

export type FormActionsVariant = NonNullable<VariantProps<typeof formActionsVariants>["variant"]>;

export interface FormActionsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof formActionsVariants> {
  /** Applies to the `joined` / `separated` grouped-control variants. */
  orientation?: "horizontal" | "vertical";
  /**
   * Optional leading hint rendered opposite the action buttons — e.g. an
   * unsaved-changes message on a settings/save footer. When set, the row
   * spreads the hint and the actions apart. Pair with `hintTone="attention"`
   * to draw the eye while the form is dirty.
   */
  hint?: React.ReactNode;
  /** Tone for `hint`. `muted` (default) or `attention` (dirty/unsaved). */
  hintTone?: "muted" | "attention";
}

export const FormActions = React.forwardRef<HTMLDivElement, FormActionsProps>(function FormActions(
  {
    children,
    className,
    hint,
    hintTone = "muted",
    orientation = "horizontal",
    role,
    variant,
    ...props
  },
  ref,
) {
  const isButtonGroup = variant === "joined" || variant === "separated";
  return (
    <div
      ref={ref}
      className={cx(
        formActionsVariants({ variant }),
        isButtonGroup && `cw-v2-form-actions--${orientation}`,
        hint != null && "cw-v2-form-actions--with-hint",
        className,
      )}
      role={role ?? (isButtonGroup ? "group" : undefined)}
      {...props}
    >
      {hint != null ? (
        <span
          className={cx(
            "cw-v2-form-actions__hint",
            hintTone === "attention" && "cw-v2-form-actions__hint--attention",
          )}
        >
          {hint}
        </span>
      ) : null}
      {children}
    </div>
  );
});
FormActions.displayName = "FormActions";
